import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, Search } from "lucide-react";
import { getDealsByDealer, DealRecord } from "@/Calls/callDeals";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";

const STATUS_COLORS: Record<string, string> = {
    PENDING:   "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    VIEWED:    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    COUNTERED: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400",
    ACCEPTED:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    DECLINED:  "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    FUNDED:    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    CANCELLED: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

function fmt(v: string | null | undefined) {
    if (!v) return "—";
    const n = Number(v);
    if (isNaN(n)) return v;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const FILTERS = ["ALL", "PENDING", "VIEWED", "COUNTERED", "ACCEPTED", "DECLINED", "FUNDED"];

export default function DealerDealsPage() {
    const { user } = useAuth();
    const dealerId = (user as any)?.dealerId ?? (user as any)?.id ?? 0;

    const [deals, setDeals] = useState<DealRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!dealerId) return;
        setLoading(true);
        (async () => {
            try {
                const data = await getDealsByDealer(
                    Number(dealerId),
                    statusFilter !== "ALL" ? statusFilter : undefined
                );
                setDeals(data);
            } catch { /* silent */ } finally {
                setLoading(false);
            }
        })();
    }, [dealerId, statusFilter]);

    const filtered = search.trim()
        ? deals.filter(d => {
            const q = search.toLowerCase();
            return (
                (d.vMake ?? "").toLowerCase().includes(q) ||
                (d.vModel ?? "").toLowerCase().includes(q) ||
                (d.vVin ?? "").toLowerCase().includes(q) ||
                String(d.deal_number).includes(q)
            );
        })
        : deals;

    // Summary counts
    const counts = deals.reduce<Record<string, number>>((acc, d) => {
        const s = d.deal_status ?? "PENDING";
        acc[s] = (acc[s] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Deals</h1>
                <p className="text-muted-foreground text-sm mt-1">Full deal history for your dealership</p>
            </div>

            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["PENDING", "ACCEPTED", "FUNDED", "DECLINED"].map(s => (
                    <Card key={s} className="p-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{s}</p>
                        <p className="text-2xl font-semibold mt-1">{counts[s] ?? 0}</p>
                    </Card>
                ))}
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search by make, model, VIN, or deal #"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {FILTERS.map(f => (
                        <button
                            key={f}
                            onClick={() => setStatusFilter(f)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                                statusFilter === f
                                    ? "bg-indigo-500 text-white"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground/30" />
                    <p className="text-muted-foreground">No deals found.</p>
                    <Link
                        to="/dealer/dealdesk"
                        className="text-sm text-indigo-600 hover:underline"
                    >
                        Open Deal Desk
                    </Link>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/50">
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Deal #</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Vehicle</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Type</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-muted-foreground">Price</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-muted-foreground">Payment</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filtered.map(deal => {
                                const status = deal.deal_status ?? "PENDING";
                                const isFinance = (deal.deal_type ?? "FINANCE") === "FINANCE";
                                return (
                                    <tr key={deal.deal_id} className="hover:bg-muted/30 transition-colors">
                                        <td className="px-4 py-3 font-mono text-muted-foreground">
                                            #{deal.deal_number || deal.deal_id}
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="font-medium">
                                                {deal.vYear} {deal.vMake} {deal.vModel}
                                            </p>
                                            {deal.vVin && (
                                                <p className="text-xs text-muted-foreground font-mono">{deal.vVin}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">
                                            {isFinance ? "Finance" : "Cash"}
                                        </td>
                                        <td className="px-4 py-3 text-right">{fmt(deal.priceVehicle)}</td>
                                        <td className="px-4 py-3 text-right font-medium text-indigo-600 dark:text-indigo-400">
                                            {isFinance
                                                ? deal.monthlypymts ? `${fmt(deal.monthlypymts)}/mo` : "—"
                                                : fmt(deal.amountDDue)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] ?? STATUS_COLORS.PENDING}`}>
                                                {status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs">
                                            {new Date(deal.deal_created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export { DealerDealsPage };
